import { useEffect, useState, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Car, Ship, Plane, Home, Watch, Palette, Users, Sparkles, Heart, ShoppingBag, Key, GitCompare, X, Check, Trophy, TrendingUp, Zap, ArrowLeft } from 'lucide-react';
import { MARKETPLACE_CATEGORIES } from '@/utils/constants';
import Nav from '@/components/Nav';
import QuickAccessBar from '@/components/QuickAccessBar';
import axios from 'axios';
import { toast } from 'sonner';
import { useCurrency } from '@/context/CurrencyContext';
import PriceDisplay from '@/components/PriceDisplay';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scroll position stored in memory (not sessionStorage)
let _savedScroll = 0;
const SCROLL_KEY = 'marketplace_scroll_position';

const iconMap = {
  Car, Ship, Plane, Home, Watch, Palette, Users,
};

// Categories that support rental
const RENTAL_CATEGORIES = ['cars', 'yachts', 'jets'];

// Categories that support comparison
const COMPARABLE_CATEGORIES = ['cars', 'yachts', 'jets', 'watches'];

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const listingTypeParam = searchParams.get('type');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'all');
  const [listingType, setListingType] = useState(listingTypeParam || 'all'); // 'all', 'buy', 'rent'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Compare functionality
  const [compareMode, setCompareMode] = useState(false);
  const [compareItems, setCompareItems] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const MAX_COMPARE_ITEMS = 3;

  // Restore scroll position when navigating back
  useLayoutEffect(() => {
    const savedPosition = _savedScroll;
    if (savedPosition && !loading) {
      // Use requestAnimationFrame to ensure DOM is fully painted
      // and a small delay to allow images/content to load
      const restoreScroll = () => {
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
          _savedScroll = 0;
        });
      };
      // Small delay to ensure content is fully rendered
      const timer = setTimeout(restoreScroll, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Save scroll position before navigating away
  const handleItemClick = (item) => {
    _savedScroll = window.scrollY;
    navigate(`/marketplace/${item.id}?category=${item.category}`);
  };

  useEffect(() => {
    fetchItems();
  }, [selectedCategory, listingType]);

  const fetchItems = async () => {
    try {
      const category = selectedCategory === 'all' ? '' : selectedCategory;
      const params = { category };
      if (listingType !== 'all') {
        params.listing_type = listingType;
      }
      const response = await axios.get(`${API}/marketplace`, { params });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load marketplace items');
    } finally {
      setLoading(false);
    }
  };

  // Check if current category supports rental
  const showRentalFilter = selectedCategory === 'all' || RENTAL_CATEGORIES.includes(selectedCategory);
  
  // Check if current category supports comparison
  const showCompareOption = selectedCategory !== 'all' && COMPARABLE_CATEGORIES.includes(selectedCategory);

  // Handle category change
  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    // Reset listing type if new category doesn't support rental
    if (cat !== 'all' && !RENTAL_CATEGORIES.includes(cat)) {
      setListingType('all');
    }
    // Reset compare when changing category
    setCompareMode(false);
    setCompareItems([]);
  };
  
  // Toggle item in compare list
  const toggleCompareItem = (item, e) => {
    e.stopPropagation();
    if (compareItems.find(i => i.id === item.id)) {
      setCompareItems(compareItems.filter(i => i.id !== item.id));
    } else if (compareItems.length < MAX_COMPARE_ITEMS) {
      setCompareItems([...compareItems, item]);
    } else {
      toast.error(`Maximum ${MAX_COMPARE_ITEMS} items for comparison`);
    }
  };
  
  // Format price — uses global currency context
  const fmtPrice = (price) => formatPrice(price);

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      {/* Navigation */}
      <Nav activePage="/marketplace" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-12 md:py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-text-secondary hover:text-gold mb-4 -ml-2" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">Luxury Marketplace</h1>
          <p className="text-text-secondary text-base sm:text-lg mb-6">Complete your dream lifestyle with curated luxury items and services</p>
          <QuickAccessBar />
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => handleCategoryChange('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' 
                ? 'bg-gold text-black hover:bg-gold-light' 
                : 'border-white/20 hover:border-gold text-white'}
              data-testid="category-all-btn"
            >
              All Categories
            </Button>
            {MARKETPLACE_CATEGORIES.map((cat) => {
              const Icon = iconMap[cat.icon];
              return (
                <Button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  className={selectedCategory === cat.id 
                    ? 'bg-gold text-black hover:bg-gold-light' 
                    : 'border-white/20 hover:border-gold text-white'}
                  data-testid={`category-${cat.id}-btn`}
                >
                  {Icon && <Icon className="mr-2 h-4 w-4" />}
                  {cat.label}
                </Button>
              );
            })}
          </div>
        </motion.div>

        {/* Buy/Rent Filter - Only show for categories that support rental */}
        {showRentalFilter && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-12"
          >
            <div className="glass-card p-4 rounded-lg border border-white/10 inline-flex gap-2">
              <span className="text-text-secondary text-sm self-center mr-2">Listing Type:</span>
              <Button
                onClick={() => setListingType('all')}
                size="sm"
                variant={listingType === 'all' ? 'default' : 'ghost'}
                className={listingType === 'all' 
                  ? 'bg-gold text-black hover:bg-gold-light' 
                  : 'text-text-secondary hover:text-white'}
                data-testid="listing-all-btn"
              >
                All
              </Button>
              <Button
                onClick={() => setListingType('buy')}
                size="sm"
                variant={listingType === 'buy' ? 'default' : 'ghost'}
                className={listingType === 'buy' 
                  ? 'bg-gold text-black hover:bg-gold-light' 
                  : 'text-text-secondary hover:text-white'}
                data-testid="listing-buy-btn"
              >
                <ShoppingBag className="mr-1 h-4 w-4" />
                Buy
              </Button>
              <Button
                onClick={() => setListingType('rent')}
                size="sm"
                variant={listingType === 'rent' ? 'default' : 'ghost'}
                className={listingType === 'rent' 
                  ? 'bg-gold text-black hover:bg-gold-light' 
                  : 'text-text-secondary hover:text-white'}
                data-testid="listing-rent-btn"
              >
                <Key className="mr-1 h-4 w-4" />
                Rent
              </Button>
            </div>
          </motion.div>
        )}

        {/* Compare Mode Toggle */}
        {showCompareOption && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 flex items-center gap-4"
          >
            <Button
              onClick={() => {
                setCompareMode(!compareMode);
                if (compareMode) setCompareItems([]);
              }}
              variant={compareMode ? 'default' : 'outline'}
              className={compareMode 
                ? 'bg-blue-500 text-white hover:bg-blue-600' 
                : 'border-white/20 hover:border-blue-400 text-white'}
              data-testid="compare-mode-btn"
            >
              <GitCompare className="mr-2 h-4 w-4" />
              {compareMode ? 'Exit Compare' : 'Compare Items'}
            </Button>
            
            {compareMode && compareItems.length > 0 && (
              <Button
                onClick={() => setShowCompareModal(true)}
                className="bg-gold text-black hover:bg-gold-light"
                data-testid="view-comparison-btn"
              >
                View Comparison ({compareItems.length}/{MAX_COMPARE_ITEMS})
              </Button>
            )}
          </motion.div>
        )}

        {/* Items Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-gold text-xl">Loading luxury items...</div>
          </div>
        ) : items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-12 text-center"
          >
            <Sparkles className="w-16 h-16 text-gold mx-auto mb-4" />
            <h3 className="font-playfair text-2xl font-semibold mb-2">Curating Luxury Items</h3>
            <p className="text-text-secondary mb-6">Our marketplace is being stocked with the finest luxury items. Check back soon or contact our concierge.</p>
            <Button className="bg-gold text-black hover:bg-gold-light" onClick={() => navigate('/concierge')} data-testid="marketplace-concierge-btn">
              Contact Concierge
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((item, index) => {
              const isSelected = compareItems.find(i => i.id === item.id);
              return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`group relative overflow-hidden bg-neutral-900/50 border transition-all duration-500 cursor-pointer ${
                  isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-white/5 hover:border-gold/30'
                }`}
                onClick={() => compareMode ? toggleCompareItem(item, { stopPropagation: () => {} }) : handleItemClick(item)}
                data-testid={`marketplace-item-${index}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img 
                    src={item.images?.[0] || 'https://images.unsplash.com/photo-1606917454663-343e58811bb1?w=800'}
                    alt={item.model}
                    className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-gold text-black px-3 py-1 text-xs font-bold uppercase">
                    {item.luxury_tier}
                  </div>
                  {/* Rental Badge */}
                  {item.available_for_rent && (
                    <div className="absolute top-4 left-4 bg-emerald-500 text-white px-3 py-1 text-xs font-bold uppercase flex items-center gap-1">
                      <Key className="w-3 h-3" />
                      Rental Available
                    </div>
                  )}
                  {/* Compare Checkbox */}
                  {compareMode && COMPARABLE_CATEGORIES.includes(item.category) && (
                    <button
                      onClick={(e) => toggleCompareItem(item, e)}
                      className={`absolute bottom-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        isSelected 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-black/60 backdrop-blur-sm text-white hover:bg-blue-500'
                      }`}
                      data-testid={`compare-checkbox-${index}`}
                    >
                      {isSelected ? <Check className="w-4 h-4" /> : <GitCompare className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <div className="p-6">
                  <div className="text-text-secondary text-sm mb-2 uppercase tracking-wider">{item.brand}</div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">{item.title || item.model}</h3>
                  <p className="text-text-secondary text-sm mb-4 line-clamp-2">{item.description}</p>
                  
                  {/* ASTIIV BASGAT Score */}
                  {item.dreamlife_score && (
                    <div className="flex items-center gap-4 mb-4 text-xs">
                      <span className="flex items-center gap-1">
                        <Trophy className="w-3 h-3 text-gold" /> {item.prestige_score || '-'}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-400" /> {item.roi_score || '-'}
                      </span>
                      <span className="flex items-center gap-1 text-gold font-semibold">
                        ⭐ {item.dreamlife_score}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-4 border-t border-white/10">
                    <div>
                      <PriceDisplay amount={item.price} className="text-gold text-xl font-bold" />
                      {item.available_for_rent && item.rental_price_daily && (
                        <div className="text-emerald-400 text-sm mt-1">
                          Rent: {formatPrice(item.rental_price_daily, { showDual: false })}/day
                        </div>
                      )}
                    </div>
                    {!compareMode && (
                      <Button 
                        size="sm" 
                        className="bg-gold text-black hover:bg-gold-light text-xs" 
                        data-testid={`item-inquire-btn-${index}`}
                        onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                      >
                        View Details
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )})}
          </div>
        )}
      </div>
      
      {/* Compare Modal */}
      <AnimatePresence>
        {showCompareModal && compareItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCompareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-obsidian border border-white/20 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 bg-obsidian z-10">
                <div>
                  <h2 className="font-playfair text-2xl font-semibold">Compare {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}</h2>
                  <p className="text-text-secondary text-sm">{compareItems.length} items selected</p>
                </div>
                <Button variant="ghost" onClick={() => setShowCompareModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-6">
                {/* Item Images */}
                <div className={`grid grid-cols-${compareItems.length} gap-4 mb-8`}>
                  {compareItems.map((item) => (
                    <div key={item.id} className="text-center">
                      <img 
                        src={item.images?.[0] || 'https://images.unsplash.com/photo-1606917454663-343e58811bb1?w=400'}
                        alt={item.model}
                        className="w-full aspect-square object-cover rounded-lg mb-4"
                      />
                      <h3 className="font-semibold text-lg">{item.brand}</h3>
                      <p className="text-gold">{item.title || item.model}</p>
                    </div>
                  ))}
                </div>
                
                {/* Comparison Table */}
                <div className="space-y-4">
                  {/* Price */}
                  <div className="grid grid-cols-${compareItems.length} gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider">Price</div>
                    {compareItems.map((item) => (
                      <div key={`price-${item.id}`} className="text-gold text-xl font-bold">{formatPrice(item.price)}</div>
                    ))}
                  </div>
                  
                  {/* ASTIIV BASGAT Score */}
                  <div className="grid gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider">ASTIIV BASGAT Score</div>
                    {compareItems.map((item) => (
                      <div key={`score-${item.id}`} className="text-xl font-bold flex items-center gap-2">
                        ⭐ <span className="text-gold">{item.dreamlife_score || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Prestige Score */}
                  <div className="grid gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-gold" /> Prestige Score
                    </div>
                    {compareItems.map((item) => (
                      <div key={`prestige-${item.id}`} className="text-lg font-semibold">{item.prestige_score || 'N/A'}/100</div>
                    ))}
                  </div>
                  
                  {/* ROI Score */}
                  <div className="grid gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-400" /> ROI Score
                    </div>
                    {compareItems.map((item) => (
                      <div key={`roi-${item.id}`} className="text-lg font-semibold">{item.roi_score || 'N/A'}/100</div>
                    ))}
                  </div>
                  
                  {/* Demand Score */}
                  <div className="grid gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider flex items-center gap-2">
                      <Zap className="w-4 h-4 text-orange-400" /> Market Demand
                    </div>
                    {compareItems.map((item) => (
                      <div key={`demand-${item.id}`} className="text-lg font-semibold">{item.demand_score || 'N/A'}/100</div>
                    ))}
                  </div>
                  
                  {/* Tier */}
                  <div className="grid gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider">Luxury Tier</div>
                    {compareItems.map((item) => (
                      <div key={`tier-${item.id}`} className="text-lg font-semibold capitalize">{item.luxury_tier || 'N/A'}</div>
                    ))}
                  </div>
                  
                  {/* Rental */}
                  <div className="grid gap-4 p-4 bg-white/5 rounded-lg" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                    <div className="col-span-full text-sm text-text-secondary mb-2 uppercase tracking-wider">Rental Available</div>
                    {compareItems.map((item) => (
                      <div key={`rental-${item.id}`} className="text-lg font-semibold">
                        {item.available_for_rent ? (
                          <span className="text-emerald-400 flex items-center gap-1"><Check className="w-4 h-4" /> Yes</span>
                        ) : (
                          <span className="text-text-secondary">No</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* View Details CTAs */}
                <div className="grid gap-4 mt-8" style={{gridTemplateColumns: `repeat(${compareItems.length}, 1fr)`}}>
                  {compareItems.map((item) => (
                    <Button
                      key={`cta-${item.id}`}
                      onClick={() => { setShowCompareModal(false); navigate(`/marketplace/${item.id}`); }}
                      className="bg-gold text-black hover:bg-gold-light"
                    >
                      View {item.brand} Details
                    </Button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MarketplacePage;